import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CalendarClock, Plus, Trash2, Church, Clock, MapPin, CheckCircle2, Circle } from "lucide-react";

const DAYS = [
  { key: 0, label: "Senin" }, { key: 1, label: "Selasa" }, { key: 2, label: "Rabu" },
  { key: 3, label: "Kamis" }, { key: 4, label: "Jumat" }, { key: 5, label: "Sabtu" }, { key: 6, label: "Minggu" },
];

const CATEGORIES = [
  { key: "IBADAH_RUTIN", label: "Ibadah Umum" },
  { key: "IBADAH_PERSATUAN", label: "Ibadah Persatuan Klasis" },
  { key: "DOA", label: "Doa & Puasa" },
  { key: "SEKOLAH_MINGGU", label: "Sekolah Minggu" },
  { key: "LATIHAN", label: "Latihan Paduan / Musik" },
  { key: "LAINNYA", label: "Lainnya" },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

export default function KalenderIbadah() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [churches, setChurches] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [tab, setTab] = useState("upcoming");
  const [form, setForm] = useState({
    church_id: user?.church_id || "",
    name: "Ibadah Umum",
    day_of_week: 6,
    time: "09:00",
    duration_min: 90,
    location: "",
    category: "IBADAH_RUTIN",
    active: true,
  });

  const canManage = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","GEMBALA_JEMAAT","SEKRETARIS_JEMAAT"].includes(user?.role);
  const isKlasisAdmin = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const q = selectedChurch ? `?church_id=${selectedChurch}` : "";
      const [s, u, c] = await Promise.all([
        api.get(`/ibadah-schedules${q}`),
        api.get(`/ibadah-schedules/upcoming?weeks=4${selectedChurch ? `&church_id=${selectedChurch}` : ""}`),
        churches.length ? Promise.resolve({ data: churches }) : api.get("/churches"),
      ]);
      setSchedules(s.data); setUpcoming(u.data);
      if (!churches.length) setChurches(c.data.filter(x => x.type === "JEMAAT"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.church_id) setSelectedChurch(user.church_id);
    load();
  }, []);

  useEffect(() => { load(); }, [selectedChurch]);

  const create = async () => {
    if (!form.church_id) { toast.error("Pilih jemaat"); return; }
    try {
      await api.post("/ibadah-schedules", form);
      toast.success("Jadwal ibadah dibuat");
      setOpenNew(false);
      setForm({ ...form, name: "Ibadah Umum" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus jadwal ibadah ini?")) return;
    try {
      await api.delete(`/ibadah-schedules/${id}`);
      toast.success("Jadwal dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/ibadah-schedules/${s.id}`, { ...s, active: !s.active });
      toast.success(!s.active ? "Jadwal diaktifkan" : "Jadwal dinonaktifkan");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group upcoming by date
  const grouped = useMemo(() => {
    const map = {};
    upcoming.forEach((u) => {
      if (!map[u.date]) map[u.date] = [];
      map[u.date].push(u);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [upcoming]);

  const churchName = (id) => churches.find(c => c.id === id)?.short_name || "—";
  const catLabel = (k) => CATEGORIES.find(c => c.key === k)?.label || k;

  const CategoryBadge = ({ category }) => {
    if (category === "IBADAH_PERSATUAN") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E5B52A] to-[#F0C86A] text-white shadow-sm border border-[#E5B52A]/50" data-testid={`badge-cat-${category}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-white" /> Persatuan Klasis
        </span>
      );
    }
    return <StatusBadge status="info" label={catLabel(category)} />;
  };
  const dayLabel = (n) => DAYS.find(d => d.key === n)?.label || "?";

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelayanan • Jadwal</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <CalendarClock className="h-7 w-7 text-secondary" /> Kalender Ibadah
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Jadwal ibadah rutin per jemaat. Sistem otomatis menghitung tanggal-tanggal ibadah 4 minggu ke depan.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isKlasisAdmin && (
            <Select value={selectedChurch || "ALL"} onValueChange={(v)=>setSelectedChurch(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="select-kalender-church"><SelectValue placeholder="Semua jemaat" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua Jemaat</SelectItem>
                {churches.map(c => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canManage && (
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="btn-new-schedule"><Plus className="h-4 w-4 mr-2" /> Jadwal Baru</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Jadwal Ibadah Rutin</DialogTitle>
                  <DialogDescription>Buat jadwal yang berulang setiap minggu. Sistem akan menampilkan otomatis di tanggal-tanggal yang tepat.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Jemaat</Label>
                    <Select value={form.church_id} onValueChange={(v)=>setForm({...form, church_id:v})} disabled={!isKlasisAdmin && !!user?.church_id}>
                      <SelectTrigger data-testid="select-form-church"><SelectValue placeholder="Pilih jemaat" /></SelectTrigger>
                      <SelectContent>{churches.map(c => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Nama Ibadah</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Ibadah Umum Minggu" data-testid="input-schedule-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Hari</Label>
                      <Select value={String(form.day_of_week)} onValueChange={(v)=>setForm({...form, day_of_week: parseInt(v,10)})}>
                        <SelectTrigger data-testid="select-schedule-day"><SelectValue /></SelectTrigger>
                        <SelectContent>{DAYS.map(d => <SelectItem key={d.key} value={String(d.key)}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Waktu Mulai</Label>
                      <Input type="time" value={form.time} onChange={(e)=>setForm({...form, time:e.target.value})} data-testid="input-schedule-time" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Durasi (menit)</Label>
                      <Input type="number" min="30" step="15" value={form.duration_min} onChange={(e)=>setForm({...form, duration_min: parseInt(e.target.value,10)})} data-testid="input-schedule-duration" />
                    </div>
                    <div>
                      <Label>Kategori</Label>
                      <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                        <SelectTrigger data-testid="select-schedule-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Lokasi</Label>
                    <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} placeholder="Gedung Utama" data-testid="input-schedule-location" />
                  </div>
                </div>
                <DialogFooter><Button onClick={create} data-testid="btn-save-schedule">Simpan Jadwal</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Jadwal 4 Minggu Mendatang</TabsTrigger>
          <TabsTrigger value="rutin" data-testid="tab-rutin">Semua Jadwal Rutin ({schedules.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : grouped.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <CalendarClock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">Belum ada jadwal ibadah. Tambah dulu di tab "Semua Jadwal Rutin".</div>
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.map(([date, items]) => (
                <Card key={date} data-testid={`upcoming-day-${date}`}>
                  <CardHeader className="pb-2 bg-primary/5 border-b border-primary/20">
                    <CardTitle className="text-sm font-heading text-primary">{fmtDate(date)}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {items.map((it) => (
                      <div key={`${it.schedule_id}-${it.date}`} className="border-b border-border last:border-0 p-4 flex items-center gap-4 hover:bg-muted/30">
                        <div className="text-center shrink-0">
                          <div className="text-[0.65rem] uppercase text-muted-foreground">Pukul</div>
                          <div className="font-mono font-bold text-lg text-primary">{it.time}</div>
                          <div className="text-[0.65rem] text-muted-foreground">{it.duration_min} min</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-semibold text-sm">{it.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                            <span className="inline-flex items-center gap-1"><Church className="h-3 w-3" /> {it.church_short_name || "—"}</span>
                            {it.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.location}</span>}
                          </div>
                        </div>
                        <CategoryBadge category={it.category} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rutin">
          {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : schedules.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <CalendarClock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">Belum ada jadwal rutin.</div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Nama</th>
                      <th className="text-left p-3">Jemaat</th>
                      <th className="text-left p-3">Hari & Waktu</th>
                      <th className="text-left p-3">Lokasi</th>
                      <th className="text-left p-3">Kategori</th>
                      <th className="text-center p-3">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((s) => (
                      <tr key={s.id} className="border-t border-border" data-testid={`row-schedule-${s.id}`}>
                        <td className="p-3 font-medium">{s.name}</td>
                        <td className="p-3 text-xs">{churchName(s.church_id)}</td>
                        <td className="p-3 text-xs whitespace-nowrap"><Clock className="h-3 w-3 inline mr-1" /> {dayLabel(s.day_of_week)} · {s.time}</td>
                        <td className="p-3 text-xs">{s.location || "—"}</td>
                        <td className="p-3 text-xs"><CategoryBadge category={s.category} /></td>
                        <td className="p-3 text-center">
                          {canManage ? (
                            <button onClick={()=>toggleActive(s)} className="cursor-pointer" data-testid={`btn-toggle-schedule-${s.id}`}>
                              {s.active ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <Circle className="h-4 w-4 text-muted-foreground inline" />}
                            </button>
                          ) : (s.active ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" /> : <Circle className="h-4 w-4 text-muted-foreground inline" />)}
                        </td>
                        <td className="p-3 text-right">
                          {canManage && (
                            <button onClick={()=>del(s.id)} className="text-rose-600 hover:text-rose-800 p-1" data-testid={`btn-del-schedule-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
